import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Clock, Building2, User, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Register = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { completeRegistration } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(true);
  const [inviteError, setInviteError] = useState(null);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const inviteToken = searchParams.get('token');

  useEffect(() => {
    if (!inviteToken) {
      setInviteError('No invite token provided. Please use the invite link sent by your admin.');
      setInviteLoading(false);
      return;
    }

    const fetchInviteInfo = async () => {
      try {
        const response = await axios.get(`${API}/auth/invite/${inviteToken}`);
        setInviteInfo(response.data);
      } catch (error) {
        const message = error.response?.data?.detail || 'Invalid or expired invite link';
        setInviteError(message);
      } finally {
        setInviteLoading(false);
      }
    };

    fetchInviteInfo();
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      await completeRegistration(inviteToken, password);
      toast.success('Registration complete! Welcome to ClockIn Pro.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (inviteLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-slate-50">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-10 h-10 rounded-xl bg-[hsl(243,65%,20%)] flex items-center justify-center text-white">
            <Clock className="w-6 h-6" />
          </div>
          <span className="text-xl font-bold font-['Manrope'] text-[hsl(243,65%,20%)]">ClockIn Pro</span>
        </div>

        {inviteError ? (
          <Card className="border-0 shadow-xl">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 rounded-full bg-[hsl(var(--error-bg))] flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-[hsl(var(--error))]" />
              </div>
              <h2 className="text-xl font-semibold font-['Manrope'] mb-2">Invalid Invite</h2>
              <p className="text-slate-500 mb-6">{inviteError}</p>
              <Button 
                variant="outline" 
                onClick={() => navigate('/login')}
                data-testid="back-to-login-btn"
              >
                Back to Login
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-['Manrope']">Complete Registration</CardTitle>
              <CardDescription>Set up your password to access your account</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Invite Info */}
              <div className="bg-slate-50 rounded-lg p-4 mb-6 space-y-3">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Name</p>
                    <p className="font-medium">{inviteInfo?.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Company</p>
                    <p className="font-medium">{inviteInfo?.company_name}</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={inviteInfo?.email || ''}
                    disabled
                    className="bg-slate-100"
                    data-testid="register-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Create a password (min. 6 characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="register-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="register-confirm-password-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
                  disabled={isLoading}
                  data-testid="register-submit-btn"
                >
                  {isLoading ? <div className="spinner" /> : 'Complete Registration'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Register;
